// A1 keystore-isolation (§13 of docs/phase4-railgun-isolation-vi.md), milestone 1.
//
// Page-side facade for the nested RAILGUN worker. The worker itself lives under
// the keystore worker and the page never holds a handle to it; this module is
// the page's only view of the worker's lifecycle. `WebViewWorker.web.tsx`
// (which owns the keystore worker) calls the setters when the worker relays a
// status message, and consumers (e.g. `appModeContext`) await
// `waitForRailgunWorkerEngine` to know the engine is up.
//
// Later milestones extend this facade with the non-secret RPC surface
// (loadWallet by id, balances, populate/prove) — the secret leg
// (encryptionKey/mnemonic) stays internal to the worker tier and never passes
// through here.

type EngineWaiter = {
  resolve: () => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

const state: { engineReady: boolean; error: string | null } = {
  engineReady: false,
  error: null
}

const waiters = new Set<EngineWaiter>()

const settleWaiter = (waiter: EngineWaiter, err?: Error) => {
  clearTimeout(waiter.timer)
  waiters.delete(waiter)
  if (err) waiter.reject(err)
  else waiter.resolve()
}

export function setRailgunWorkerEngineReady(): void {
  state.engineReady = true
  state.error = null
  Array.from(waiters).forEach((w) => settleWaiter(w))
}

export function setRailgunWorkerEngineError(message: string): void {
  state.error = message || 'railgun worker engine init failed'
  const err = new Error(state.error)
  Array.from(waiters).forEach((w) => settleWaiter(w, err))
}

export function isRailgunWorkerEngineReady(): boolean {
  return state.engineReady
}

/**
 * Resolves once the nested RAILGUN worker reports its engine ready, rejects if
 * the worker reports an init error or the timeout elapses. Resolves
 * immediately when the engine is already up.
 */
export function waitForRailgunWorkerEngine(timeoutMs: number): Promise<void> {
  if (state.engineReady) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const waiter: EngineWaiter = {
      resolve,
      reject,
      timer: setTimeout(() => {
        waiters.delete(waiter)
        reject(new Error('railgun worker engine did not report ready in time'))
      }, timeoutMs)
    }
    waiters.add(waiter)
  })
}

// Worker-RPC transport (page ↔ keystore worker). Carries the railgun worker's
// non-secret results back to the page: wallet identity (create/load), balances,
// transaction history. The page never sends or receives the seed/encryptionKey.
export type RailgunWalletPublicInfo = {
  walletId: string
  railgunAddress: string
  viewingKey: string
}

type WalletRpcPending = {
  resolve: (info: any) => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

const walletRpcPending = new Map<string, WalletRpcPending>()
let walletRpcSeq = 0

export function nextRailgunWalletRequestId(): string {
  walletRpcSeq += 1
  return `rgw-${Date.now()}-${walletRpcSeq}`
}

/** Register a waiter for a worker-RPC result keyed by requestId (paired with a dispatch). */
export function awaitRailgunWalletResult<T = any>(requestId: string, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const pending: WalletRpcPending = {
      resolve,
      reject,
      timer: setTimeout(() => {
        walletRpcPending.delete(requestId)
        reject(new Error('railgun worker request timed out'))
      }, timeoutMs)
    }
    walletRpcPending.set(requestId, pending)
  })
}

/** Called by `WebViewWorker.web.tsx` when the keystore worker relays an RPC result. */
export function resolveRailgunWalletResult(
  requestId: string,
  payload: { info?: any; error?: string }
): void {
  const pending = walletRpcPending.get(requestId)
  if (!pending) return
  walletRpcPending.delete(requestId)
  clearTimeout(pending.timer)
  if (payload.error) {
    pending.reject(new Error(payload.error))
  } else {
    pending.resolve(payload.info)
  }
}
